// src/auth/google.ts
// GIS-based OAuth with silent refresh + StrictMode guards.
// Provides token + change notifications. Does NOT import gapi (avoids cycles).

type TokenResp = google.accounts.oauth2.TokenResponse;

let tokenClient: google.accounts.oauth2.TokenClient | null = null;
let refreshTimer: number | null = null;
let initStarted = false;
let silentInFlight = false;
let interactiveInFlight = false;

const K_TOKEN = 'gs_token';
const K_EXP   = 'gs_expires_at';

// Allow other modules (gapi client) to subscribe to raw token updates
const tokenConsumers = new Set<(token: string) => void>();
export function registerTokenConsumer(cb: (token: string) => void) {
  tokenConsumers.add(cb); 
  return () => tokenConsumers.delete(cb);
}

// App-level listeners for "authed?" (boolean) changes
const listeners = new Set<() => void>();
export function onAuthChange(cb: () => void) { 
  listeners.add(cb); 
  return () => listeners.delete(cb); 
}
const notify = () => listeners.forEach(fn => fn());

export function getAccessToken(): string | null {
  const t = localStorage.getItem(K_TOKEN);
  const exp = Number(localStorage.getItem(K_EXP) || 0);
  return t && Date.now() < exp ? t : null;
}

function ensureGisReady(): Promise<void> {
  return new Promise(res => {
    if ((window as any).google?.accounts?.oauth2) return res();
    const i = setInterval(() => {
      if ((window as any).google?.accounts?.oauth2) { 
        clearInterval(i); 
        res(); 
      }
    }, 30);
  });
}

function setToken(resp: TokenResp) {
  const safety = 60; // refresh 60s early
  const exp = Date.now() + ((resp.expires_in ?? 3600) - safety) * 1000;

  localStorage.setItem(K_TOKEN, resp.access_token);
  localStorage.setItem(K_EXP, String(exp));

  // Feed token to any consumers (e.g., gapi.client.setToken)
  tokenConsumers.forEach(fn => fn(resp.access_token));

  if (refreshTimer) clearTimeout(refreshTimer);
  refreshTimer = window.setTimeout(requestSilent, Math.max(5000, exp - Date.now()));
  notify();
}

export async function initGoogleAuth() {
  if (initStarted) return;          // StrictMode guard
  initStarted = true;

  await ensureGisReady();
  tokenClient = google.accounts.oauth2.initTokenClient({
    client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID || '286213465630-ugru3c9sdmfe6sbho1jvfr06hsjrvsg0.apps.googleusercontent.com',
    scope: 'https://www.googleapis.com/auth/spreadsheets',
    ux_mode: 'redirect',            // prevent popup blocking by browsers
    redirect_uri: window.location.origin,
    callback: (resp) => {
      if ((resp as any).error) { 
        console.error('[GIS]', resp); 
        return; 
      }
      setToken(resp);
    },
  });

  // If a token already exists, schedule its refresh; else try one silent attempt.
  const existing = getAccessToken();
  if (existing) {
    const exp = Number(localStorage.getItem(K_EXP) || 0);
    refreshTimer = window.setTimeout(requestSilent, Math.max(5000, exp - Date.now()));
    notify();
  }
}

export function requestSilent() {
  if (!tokenClient) return;
  if (silentInFlight) return;
  silentInFlight = true;
  tokenClient.requestAccessToken({ prompt: '' });
  setTimeout(() => { silentInFlight = false; }, 2000);
}

export function requestInteractive() {
  if (!tokenClient) return;
  if (getAccessToken()) { 
    notify(); 
    return; 
  }  // already authed
  if (interactiveInFlight) return;
  interactiveInFlight = true;
  tokenClient.requestAccessToken({ prompt: 'consent' });
  setTimeout(() => { interactiveInFlight = false; }, 4000);
}

export function signOut() {
  localStorage.removeItem(K_TOKEN);
  localStorage.removeItem(K_EXP);
  if (refreshTimer) clearTimeout(refreshTimer);
  notify();
}

// Extend window interface for TypeScript
declare global {
  interface Window {
    google: {
      accounts: {
        oauth2: {
          initTokenClient: (config: any) => any;
          TokenResponse: any;
        };
      };
    };
  }
}